
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { Sparkles, AlertTriangle, Info } from 'lucide-react'
import { STATE_INCOME_RANGES } from '../config/aca.config'

interface EmploymentPlanStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    [key: string]: any
}

// 2026 Federal Poverty Level (FPL) guidelines
const FPL_2026: { [key: number]: number } = {
    1: 15060,
    2: 20440,
    3: 25820,
    4: 31200,
    5: 36580,
    6: 41960,
    7: 47340,
    8: 52720
}

const getFPL = (householdSize: number): number => {
    if (householdSize <= 8) {
        return FPL_2026[householdSize] || FPL_2026[1]
    }
    return FPL_2026[8] + (householdSize - 8) * 5380
}

// Type of work options for self-employed
const SELF_EMPLOYED_WORK_TYPES = [
    'Please Select',
    'Construction/Contractor',
    'Consulting',
    'Real Estate',
    'Retail/Sales',
    'Food Service/Restaurant',
    'Transportation/Delivery',
    'Healthcare Services',
    'Beauty/Personal Care',
    'Technology/IT',
    'Creative/Arts',
    'Cleaning Services',
    'Landscaping/Lawn Care',
    'Home Repair/Handyman',
    'Childcare',
    'Other'
]

export default function EmploymentPlanStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6
}: EmploymentPlanStepProps) {
    const [errors, setErrors] = useState<ValidationErrors>({})

    // Get available carriers from verification step
    const availableCarriers = enrollmentData.availableCarriers || []

    // Calculate household size from previous step data
    useEffect(() => {
        let size = 1 // Client
        if (enrollmentData.maritalStatus === '3') {
            size += 1
        }
        if (enrollmentData.dependentsList && Array.isArray(enrollmentData.dependentsList)) {
            size += enrollmentData.dependentsList.filter((d: any) => d.onPolicy === 'Yes').length
        }

        if (enrollmentData.householdSize !== size) {
            updateEnrollmentData('householdSize', size)
        }
    }, [enrollmentData.maritalStatus, enrollmentData.dependentsList])

    // Check for Medicaid eligibility warning (too low income)
    const medicaidWarning = useMemo(() => {
        const householdSize = enrollmentData.householdSize || 1
        const annualIncome = parseFloat(enrollmentData.annualIncome) || 0

        if (annualIncome === 0) return null

        const fpl = getFPL(householdSize)
        const medicaidThreshold = fpl * 1.38

        if (annualIncome < medicaidThreshold) {
            return {
                show: true,
                threshold: medicaidThreshold,
                fpl: fpl,
                percentage: Math.round((annualIncome / fpl) * 100)
            }
        }

        return null
    }, [enrollmentData.householdSize, enrollmentData.annualIncome])

    // Check for high income warning (too high for subsidies)
    const highIncomeWarning = useMemo(() => {
        const householdSize = enrollmentData.householdSize || 1
        const annualIncome = parseFloat(enrollmentData.annualIncome) || 0

        if (annualIncome === 0) return null

        const fpl = getFPL(householdSize)
        const subsidyThreshold = fpl * 4.0

        if (annualIncome > subsidyThreshold) {
            return {
                show: true,
                threshold: subsidyThreshold,
                fpl: fpl,
                percentage: Math.round((annualIncome / fpl) * 100)
            }
        }

        return null
    }, [enrollmentData.householdSize, enrollmentData.annualIncome])

    // Auto-suggest income based on state and household size
    const suggestIncome = useCallback(() => {
        const householdSize = enrollmentData.householdSize || 1
        const stateCode = enrollmentData.clientState

        // Get income range for household size (use default if state-specific not available)
        const ranges = STATE_INCOME_RANGES[stateCode] || STATE_INCOME_RANGES['default']
        const rangeIndex = Math.min(householdSize - 1, ranges.length - 1)
        const range = ranges[rangeIndex]

        if (range) {
            // Generate random income within the range (biased toward middle for realism)
            const midPoint = (range.min + range.max) / 2
            const variance = (range.max - range.min) * 0.3 // 30% variance from midpoint
            const randomOffset = (Math.random() - 0.5) * 2 * variance
            const suggestedIncome = Math.round(midPoint + randomOffset)

            updateEnrollmentData('annualIncome', suggestedIncome.toString())
        }
    }, [enrollmentData.householdSize, enrollmentData.clientState, updateEnrollmentData])

    const handleNext = useCallback(async (): Promise<boolean> => {
        const validationErrors: ValidationErrors = {}

        if (!enrollmentData.employmentType) {
            validationErrors.employmentType = 'Employment type is required'
        }
        if (!enrollmentData.annualIncome) {
            validationErrors.annualIncome = 'Annual income is required'
        }

        // Validate type of work for self-employed
        if (enrollmentData.employmentType === 'Self-Employed' && !enrollmentData.typeOfWork) {
            validationErrors.typeOfWork = 'Type of work is required for self-employed'
        }

        setErrors(validationErrors)
        return Object.keys(validationErrors).length === 0
    }, [enrollmentData])

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 2}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            onNext={handleNext}
            isNavigating={isNavigating}
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 3: Employment & Plan</h3>
                        <p className="text-sm text-accent-600">Employment, income, and health plan selection.</p>
                    </div>
                </div>

                <div className="space-y-8">
                    {/* SECTION: Employment Information */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Employment Information</h4>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Household Size</label>
                                    <input
                                        type="number"
                                        value={enrollmentData.householdSize || 1}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md cursor-not-allowed"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Auto-calculated from Step 2 (Spouse and Dependents)</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type <span className="text-red-500">*</span></label>
                                    <select
                                        value={enrollmentData.employmentType || ''}
                                        onChange={(e) => updateEnrollmentData('employmentType', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.employmentType ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Please Select</option>
                                        <option value="Employed">Employed</option>
                                        <option value="Self-Employed">Self-Employed</option>
                                    </select>
                                    {errors.employmentType && <p className="mt-1 text-sm text-red-600">{errors.employmentType}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Annual Income <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <span className="text-gray-500 sm:text-sm">$</span>
                                        </div>
                                        <input
                                            type="number"
                                            placeholder="0.00"
                                            value={enrollmentData.annualIncome || ''}
                                            onChange={(e) => updateEnrollmentData('annualIncome', e.target.value)}
                                            className={`w-full pl-7 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.annualIncome ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Includes Spouse Income If "Married File Joint"</p>
                                    {errors.annualIncome && <p className="mt-1 text-sm text-red-600">{errors.annualIncome}</p>}
                                </div>

                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={suggestIncome}
                                        className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 transition-all duration-200 flex items-center justify-center gap-2 font-medium"
                                    >
                                        <Sparkles className="w-4 h-4" />
                                        Auto-Suggest Income
                                    </button>
                                </div>
                            </div>

                            {enrollmentData.employmentType === 'Employed' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Employer's Name</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.employerName || ''}
                                        onChange={(e) => updateEnrollmentData('employerName', e.target.value)}
                                        placeholder="Company Name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {enrollmentData.employmentType === 'Self-Employed' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Type of Work <span className="text-red-500">*</span></label>
                                    <select
                                        value={enrollmentData.typeOfWork || ''}
                                        onChange={(e) => updateEnrollmentData('typeOfWork', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.typeOfWork ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        {SELF_EMPLOYED_WORK_TYPES.map(type => (
                                            <option key={type} value={type === 'Please Select' ? '' : type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.typeOfWork && <p className="mt-1 text-sm text-red-600">{errors.typeOfWork}</p>}
                                </div>
                            )}

                            {/* Medicaid Warning - Income Too Low */}
                            {medicaidWarning && (
                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <AlertTriangle className="h-5 w-5 text-yellow-400" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-yellow-800">Medicaid Eligibility Warning</h3>
                                            <div className="mt-2 text-sm text-yellow-700">
                                                <p>
                                                    Based on household size of <strong>{enrollmentData.householdSize}</strong> and income of <strong>${parseFloat(enrollmentData.annualIncome || 0).toLocaleString()}</strong>,
                                                    client may qualify for Medicaid instead.
                                                </p>
                                                <p className="mt-2">
                                                    Income is <strong>{medicaidWarning.percentage}% of FPL</strong>, below 138% FPL threshold (${medicaidWarning.threshold.toLocaleString()}).
                                                </p>
                                                <p className="mt-2 font-semibold">
                                                    Client may be disqualified from ACA and should apply for Medicaid.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* High Income Warning */}
                            {highIncomeWarning && (
                                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <Info className="h-5 w-5 text-blue-400" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-blue-800">No Subsidy Eligibility</h3>
                                            <div className="mt-2 text-sm text-blue-700">
                                                <p>
                                                    Income is <strong>{highIncomeWarning.percentage}% of FPL</strong>, above 400% FPL threshold (${highIncomeWarning.threshold.toLocaleString()}).
                                                </p>
                                                <p className="mt-2 font-semibold">
                                                    Client will pay full price without government subsidies.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SECTION: Health Insurance Plan */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Health Insurance Plan Selection</h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Preferred Carrier
                                </label>
                                {availableCarriers.length > 0 ? (
                                    <select
                                        value={enrollmentData.selectedCarrier || ''}
                                        onChange={(e) => {
                                            updateEnrollmentData('selectedCarrier', e.target.value)
                                            updateEnrollmentData('carrierDesired', e.target.value)
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select a Carrier (Optional)</option>
                                        {availableCarriers.map((carrier: any) => (
                                            <option key={carrier.id} value={carrier.code}>
                                                {carrier.name} {carrier.commissionRate !== null ? `($${carrier.commissionRate} commission)` : ''}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            value={enrollmentData.carrierDesired || ''}
                                            onChange={(e) => updateEnrollmentData('carrierDesired', e.target.value)}
                                            placeholder="e.g. Blue Cross, Aetna, etc."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            No carriers loaded from verification. You can type a carrier preference.
                                        </p>
                                    </>
                                )}
                            </div>

                            {/* Show available carriers from state */}
                            {availableCarriers.length > 0 && (
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <h5 className="font-semibold text-green-800 mb-2">Available Carriers for {enrollmentData.clientState}:</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {availableCarriers.map((carrier: any) => (
                                            <div
                                                key={carrier.id}
                                                className={`p-2 rounded border text-sm cursor-pointer transition-all ${
                                                    enrollmentData.selectedCarrier === carrier.code
                                                        ? 'bg-green-200 border-green-500 font-semibold'
                                                        : 'bg-white border-green-100 hover:border-green-300'
                                                }`}
                                                onClick={() => {
                                                    updateEnrollmentData('selectedCarrier', carrier.code)
                                                    updateEnrollmentData('carrierDesired', carrier.name)
                                                }}
                                            >
                                                <span className="text-gray-800">{carrier.name}</span>
                                                {carrier.commissionRate !== null && (
                                                    <span className="block text-green-600 text-xs">${carrier.commissionRate}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Customer Notes
                                </label>
                                <textarea
                                    rows={4}
                                    value={enrollmentData.customerNotes || ''}
                                    onChange={(e) => updateEnrollmentData('customerNotes', e.target.value)}
                                    placeholder="Any special needs or notes about the client's health plan preferences..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}
